import React, { forwardRef, useMemo } from "react";
import ReactTable from "react-table-6";
import classNames from "classnames";
import DataTablePagination from "./DataTablePagination";
import DataTableCell from "./DataTableCell";
import DataTableTbody from "./DataTableTbody";
import { getVisibleTableData } from "./column-sizing";
import {
  createTextMeasurer,
  normalizeDataTableColumns,
} from "./column-normalization";
import getDataTableStatus from "./data-table-status";
import useDataTablePagination from "./use-data-table-pagination";
import useTableContainerMetrics from "./use-table-container-metrics";
// Desactivado: con `data` sin definir el efecto de useSmoothExpansion
// (setState en cada cambio de `data`) entraba en un bucle infinito de renders
// que congelaba la pantalla al entrar a un módulo y tumbaba la aplicación.
// import useSmoothExpansion from "./use-smooth-expansion";

export { normalizeDataTableColumns };

const HiddenNoData = () => null;

// Referencia estable: un `[]` nuevo en cada render invalida los useMemo y
// dispara los efectos que dependen de `data` en cada render.
const EMPTY_DATA = [];

const DataTable = forwardRef(
  (
    {
      columns = [],
      data = EMPTY_DATA,
      className,
      getPaginationProps,
      PaginationComponent = DataTablePagination,
      showCellTooltips = true,
      TdComponent,
      totalRows,
      defaultPage = 0,
      defaultPageSize = 15,
      manual = false,
      page,
      pageSize,
      pageSizeOptions,
      onPageChange,
      onPageSizeChange,
      showPagination = true,
      ...tableProps
    },
    ref,
  ) => {
    const {
      availablePageSizes,
      currentPage,
      currentPageSize,
      handlePageChange,
      handlePageSizeChange,
      isPageControlled,
      isPageSizeControlled,
    } = useDataTablePagination({
      data,
      defaultPage,
      defaultPageSize,
      manual,
      onPageChange,
      onPageSizeChange,
      page,
      pageSize,
      pageSizeOptions,
    });
    const visibleData = useMemo(
      () =>
        getVisibleTableData({
          data,
          page: currentPage,
          pageSize: currentPageSize,
          manual,
        }),
      [currentPage, currentPageSize, data, manual],
    );
    const { containerRef, width: containerWidth, font } =
      useTableContainerMetrics();
    const measureText = useMemo(() => createTextMeasurer(font), [font]);
    const normalizedColumns = useMemo(
      () =>
        normalizeDataTableColumns(columns, visibleData, {
          containerWidth,
          measureText,
        }),
      [columns, containerWidth, measureText, visibleData],
    );
    const resolvePaginationProps = (state, rowInfo, column, instance) => ({
      ...(getPaginationProps
        ? getPaginationProps(state, rowInfo, column, instance)
        : {}),
      ...(Number.isFinite(totalRows) ? { totalRows } : {}),
    });
    // Desactivado junto con useSmoothExpansion (ver import): la tabla vuelve a
    // usar SubComponent, expanded y getTrGroupProps tal como llegan en tableProps.
    // const expansion = useSmoothExpansion({
    //   SubComponent: tableProps.SubComponent,
    //   expanded: tableProps.expanded,
    //   onExpandedChange: tableProps.onExpandedChange,
    //   page: currentPage,
    //   data,
    // });
    // const resolveTrGroupProps = (state, rowInfo, column, instance) => {
    //   const own = tableProps.getTrGroupProps
    //     ? tableProps.getTrGroupProps(state, rowInfo, column, instance) || {}
    //     : {};
    //   const smooth = expansion.getTrGroupProps
    //     ? expansion.getTrGroupProps(state, rowInfo) || {}
    //     : {};
    //   return {
    //     ...own,
    //     className: classNames(own.className, smooth.className) || undefined,
    //   };
    // };
    const status = getDataTableStatus({
      columns,
      data,
      loading: tableProps.loading,
      noDataText: tableProps.noDataText,
    });

    return (
      <div ref={containerRef} className="dhn-data-table-container">
        <ReactTable
          ref={ref}
          {...tableProps}
          // Desactivado junto con useSmoothExpansion (ver import).
          // {...(expansion.SubComponent ? { SubComponent: expansion.SubComponent } : {})}
          // {...(expansion.onExpandedChange
          //   ? { expanded: expansion.expanded, onExpandedChange: expansion.onExpandedChange }
          //   : {})}
          // getTrGroupProps={resolveTrGroupProps}
          data={data}
          defaultPage={defaultPage}
          defaultPageSize={defaultPageSize}
          pageSizeOptions={availablePageSizes}
          manual={manual}
          {...(isPageControlled ? { page } : {})}
          {...(isPageSizeControlled ? { pageSize } : {})}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          showPagination={showPagination}
          // ReactTable rellena con filas vacías hasta el tamaño de página; la
          // tabla muestra solo sus filas reales (sin filas, el cuerpo reserva
          // su propio alto mínimo). Una pantalla puede fijar minRows.
          minRows={status.hasRows ? tableProps.minRows ?? 0 : 0}
          columns={normalizedColumns}
          className={classNames("dhn-data-table", className, {
            "dhn-data-table--loading": tableProps.loading,
            "dhn-data-table--skeleton": status.isLoadingWithoutRows,
            "dhn-data-table--without-empty-state": status.hideEmptyState,
            "dhn-data-table--without-columns": !status.hasColumns,
            "dhn-data-table--empty": status.isEmpty,
          })}
          TbodyComponent={DataTableTbody}
          getTbodyProps={() => ({
            emptyText: status.emptyText,
            showSkeleton: status.isLoadingWithoutRows,
          })}
          NoDataComponent={HiddenNoData}
          PaginationComponent={PaginationComponent}
          TdComponent={
            TdComponent || (showCellTooltips ? DataTableCell : undefined)
          }
          getPaginationProps={resolvePaginationProps}
          loadingText={tableProps.loadingText || "Cargando..."}
          noDataText={status.noDataText}
          previousText={tableProps.previousText || "Anterior"}
          nextText={tableProps.nextText || "Siguiente"}
          pageText={tableProps.pageText || "Página"}
          ofText={tableProps.ofText || "de"}
          rowsText={tableProps.rowsText || "filas"}
        />
      </div>
    );
  },
);

DataTable.displayName = "DataTable";

export default DataTable;
